import {useEffect, useMemo, useRef, useState} from 'react';
import {Search, X} from 'lucide-react';
import {Input} from '@/components/ui/Input';
import {cn} from '@/lib/utils/cn';
import {getGroupName, searchGroups} from '@/lib/utils/groups';

interface GroupSearchSelectProps {
  /** Selected group code, or null for "all groups". */
  value: string | null;
  onChange: (code: string | null) => void;
  className?: string;
}

// There are ~180 groups, so the list is a search box rather than a dropdown:
// type a code (`WEB`) or any part of a name (`raleigh`) to narrow it.
export function GroupSearchSelect({
  value,
  onChange,
  className,
}: GroupSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const results = useMemo(() => searchGroups(query), [query]);
  // "All groups" is index 0 of the option list whenever it's shown.
  const showAllOption = value !== null || query.trim() === '';
  const optionCount = results.length + (showAllOption ? 1 : 0);

  // Close on outside click — the panel isn't portalled, so a plain document
  // listener is enough.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({block: 'nearest'});
  }, [activeIndex, open]);

  const select = (code: string | null) => {
    onChange(code);
    setQuery('');
    setOpen(false);
  };

  const commitActive = () => {
    if (showAllOption && activeIndex === 0) return select(null);
    const option = results[activeIndex - (showAllOption ? 1 : 0)];
    if (option) select(option.code);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (optionCount === 0) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(i => (i + step + optionCount) % optionCount);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open) commitActive();
      else setOpen(true);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  const selectedLabel = value ? `${value} — ${getGroupName(value)}` : '';

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={open ? query : selectedLabel}
        onChange={e => {
          setQuery(e.target.value);
          setActiveIndex(0);
          setOpen(true);
        }}
        onFocus={() => {
          setQuery('');
          setActiveIndex(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        placeholder={value ? '' : 'Search groups'}
        className={cn('pl-8', value && !open && 'pr-8')}
        aria-label="Group"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {value && !open && (
        <button
          type="button"
          onClick={() => select(null)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label="Clear group filter"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-50 mt-1 max-h-[60vh] w-full min-w-[16rem] overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
        >
          {showAllOption && (
            <Option
              active={activeIndex === 0}
              selected={value === null}
              onSelect={() => select(null)}
              onHover={() => setActiveIndex(0)}
            >
              <span className="text-muted-foreground">All groups</span>
            </Option>
          )}
          {results.map((option, i) => {
            const index = i + (showAllOption ? 1 : 0);
            return (
              <Option
                key={option.code}
                active={activeIndex === index}
                selected={value === option.code}
                onSelect={() => select(option.code)}
                onHover={() => setActiveIndex(index)}
              >
                <span className="font-mono text-xs">{option.code}</span>
                <span className="truncate text-muted-foreground">
                  {option.name}
                </span>
              </Option>
            );
          })}
          {results.length === 0 && (
            <li className="px-2 py-6 text-center text-sm text-muted-foreground">
              No groups match “{query}”.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function Option({
  active,
  selected,
  onSelect,
  onHover,
  children,
}: {
  active: boolean;
  selected: boolean;
  onSelect: () => void;
  onHover: () => void;
  children: React.ReactNode;
}) {
  return (
    <li
      role="option"
      aria-selected={selected}
      data-active={active}
      // mousedown, not click — the input's blur would otherwise race the select.
      onMouseDown={e => {
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={onHover}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
        active && 'bg-accent text-accent-foreground',
        selected && 'font-medium'
      )}
    >
      {children}
    </li>
  );
}
